import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaSearch,
  FaPlus,
  FaList
} from 'react-icons/fa';
import {
  getAffiliates,
  updateAffiliateStatus,
  deleteAffiliate
} from '../../services/companyAuthService';

const AffiliateList = () => {
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all'
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [affiliateToDelete, setAffiliateToDelete] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [affiliateToToggle, setAffiliateToToggle] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  

  const groupAffiliates = (affiliates) => {
    const groupedMap = new Map();

    affiliates.forEach(affiliate => {
      const key = `${affiliate.email}-${affiliate.instagramId || ''}`;

      if (!groupedMap.has(key)) {
        // Ensure campaigns is an array and remove duplicates
        const uniqueCampaigns = Array.from(new Set((affiliate.campaigns || []).map(c => c._id)))
          .map(id => affiliate.campaigns.find(c => c._id === id));

        groupedMap.set(key, {
          ...affiliate,
          campaigns: uniqueCampaigns,
          campaignCount: uniqueCampaigns.length
        });
      } else {
        const existingAffiliate = groupedMap.get(key);
        // Merge campaigns, avoiding duplicates
        const existingCampaignIds = new Set(existingAffiliate.campaigns.map(c => c._id));
        const newCampaigns = (affiliate.campaigns || [])
          .filter(c => !existingCampaignIds.has(c._id));

        existingAffiliate.campaigns = [...existingAffiliate.campaigns, ...newCampaigns];
        existingAffiliate.campaignCount = existingAffiliate.campaigns.length;
      }
    });

    return Array.from(groupedMap.values());
  };

  // const fetchAffiliates = async () => {
  //   try {
  //     setLoading(true);
  //     const data = await getAffiliates({
  //       status: filters.status !== 'all' ? filters.status : undefined,
  //       type: filters.type !== 'all' ? filters.type : undefined,
  //       search: searchTerm || undefined
  //     });
  //     // Group affiliates before setting state
  //     const groupedData = groupAffiliates(data);
  //     setAffiliates(groupedData);
  //   } catch (error) {
  //     console.error('Error fetching affiliates:', error);
  //     toast.error(error.message || 'Failed to fetch affiliates');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchAffiliates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAffiliates({
        status: filters.status !== 'all' ? filters.status : undefined,
        type: filters.type !== 'all' ? filters.type : undefined,
        search: searchTerm || undefined
      });
      const groupedData = groupAffiliates(data);
      setAffiliates(groupedData);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch affiliates');
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm]);

  useEffect(() => {
    fetchAffiliates();
  }, [fetchAffiliates]);

  const confirmStatusToggle = async () => {
    try {
      await updateAffiliateStatus(affiliateToToggle, newStatus);
      toast.success(`Affiliate status updated to ${newStatus}`);
      fetchAffiliates();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.message || 'Failed to update affiliate status');
    } finally {
      setShowStatusModal(false);
      setAffiliateToToggle(null);
      setNewStatus('');
    }
  };

  const handleDeleteClick = (affiliate) => {
    // Check if affiliate has any accepted campaigns
    const hasAcceptedCampaigns = affiliate.campaigns?.some(c => c.status === 'accepted');
    
    if (hasAcceptedCampaigns) {
      toast.error('Cannot delete affiliate with accepted campaigns. Please remove individual campaigns instead.');
      return;
    }
    
    setAffiliateToDelete(affiliate._id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteAffiliate(affiliateToDelete);
      toast.success('Affiliate deleted successfully');
      fetchAffiliates();
      setShowDeleteModal(false);
      setAffiliateToDelete(null);
    } catch (error) {
      console.error('Error deleting affiliate:', error);
      toast.error(error.message || 'Failed to delete affiliate');
    }
  };

  const handleEdit = (id) => {
    navigate(`/company/dashboard/affiliates/edit/${id}`);
  };

  const handleView = (id) => {
    navigate(`/company/dashboard/affiliates/view/${id}`);
  };

  const handleCreate = () => {
    navigate('/company/dashboard/affiliates/create');
  };

  const handleViewCampaigns = (email) => {
    navigate(`/company/dashboard/affiliates/affiliate-campaigns/${encodeURIComponent(email)}`);
  };

  const filteredAffiliates = affiliates.filter(affiliate => {
    const matchesSearch =
      affiliate.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      affiliate.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      affiliate.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (affiliate.companyName && affiliate.companyName.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = filters.status === 'all' || affiliate.status === filters.status;
    const matchesType = filters.type === 'all' || affiliate.affiliateType === filters.type;

    return matchesSearch && matchesStatus && matchesType;
  }).sort((a, b) => {
    if (a.status === 'inactive' && b.status === 'active') return 1;
    if (a.status === 'active' && b.status === 'inactive') return -1;
    return 0;
  });

  return (
    <>
      <div className="flex flex-col lg:flex-row min-h-full bg-gray-100">
        <div className="flex-1 p-2 md:p-4 lg:p-6">
          <div className="bg-white shadow-md rounded-lg p-4 lg:p-6 mb-10 lg:mb-0">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold hidden lg:block">Affiliate Management</h2>
                <h2 className="text-xl font-bold lg:hidden">Affiliates</h2>
              </div>
              <button
                onClick={handleCreate}
                className="bg-orange-500 text-white px-2 py-1 lg:px-4 lg:py-2 rounded-lg flex items-center gap-2 hover:bg-orange-600 transition-colors"
              >
                <FaPlus />
                <span className="hidden lg:inline">Invite New Affiliate</span>
                <span className="lg:hidden">Add Affiliate</span>
              </button>
            </div>

            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search affiliates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>

              {/* <div className="flex gap-4">
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  <option value="expired">Expired</option>
                </select>

                <select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="all">All Types</option>
                  <option value="individual">Individual</option>
                  <option value="influencer">Influencer</option>
                  <option value="company">Company</option>
                </select>
              </div> */}
              <div className="flex flex-wrap gap-4">
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="px-4 py-2 sm:px-4 sm:py-2 px-2 py-1 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  <option value="expired">Expired</option>
                </select>

                <select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  className="px-4 py-2 sm:px-4 sm:py-2 px-2 py-1 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="all">All Types</option>
                  <option value="individual">Individual</option>
                  <option value="influencer">Influencer</option>
                  <option value="company">Company</option>
                </select>
              </div>

            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Instagram ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Campaigns
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                        </div>
                      </td>
                    </tr>
                  ) : filteredAffiliates.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                        No affiliates found
                      </td>
                    </tr>
                  ) : (
                    filteredAffiliates.map((affiliate) => (
                      <tr key={`${affiliate.email}-${affiliate.instagramId || ''}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {affiliate.firstName && affiliate.lastName 
                                  ? `${affiliate.firstName} ${affiliate.lastName}`
                                  : affiliate.username || 'Unnamed Affiliate'}
                              </div>
                              {affiliate.companyName && (
                                <div className="text-sm text-gray-500">
                                  {affiliate.companyName}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {affiliate.affiliateType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {affiliate.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {affiliate.instagramId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleViewCampaigns(affiliate.email)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 flex items-center gap-1"
                            title="View Campaigns"
                          >
                            <FaList />
                            <span className="text-xs">View ({affiliate.campaignCount || 0})</span>
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleView(affiliate._id)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="View Details"
                            >
                              <FaEye />
                            </button>
                            <button
                              onClick={() => handleEdit(affiliate._id)}
                              className="text-orange-600 hover:text-orange-900 p-1 rounded hover:bg-orange-50"
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(affiliate)}
                              className={`p-1 rounded transition-colors ${
                                affiliate.campaigns?.some(c => c.status === 'accepted')
                                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                                  : 'text-red-600 hover:text-red-800 hover:bg-red-50'
                              }`}
                              title={
                                affiliate.campaigns?.some(c => c.status === 'accepted')
                                  ? 'Cannot delete affiliate with accepted campaigns'
                                  : 'Delete affiliate'
                              }
                              disabled={affiliate.campaigns?.some(c => c.status === 'accepted')}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this affiliate invitation? This will remove all pending and rejected campaign invitations. This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setAffiliateToDelete(null);
                }}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Confirmation Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirm Status Change
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to {newStatus === 'active' ? 'activate' : 'deactivate'} this affiliate?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setAffiliateToToggle(null);
                  setNewStatus('');
                }}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusToggle}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                {newStatus === 'active' ? 'Activate' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AffiliateList; 